import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import MuiDrawer from "@mui/material/Drawer";
import Grid from "@/config/Grid/Grid";
import Button from "@/components/Button/Button";
import IconButton from "@/components/IconButton/IconButton";
import TextComponent from "@/components/TextComponent/TextComponent";
import TextField from "@/components/TextField/TextField";
import { drawerStyles } from "@/components/Drawer/Drawer.styles";
import drawerCss from "@/components/Drawer/Drawer.module.scss";
import { useCartStore } from "@/store/useCartStore";
import { clearTipDraft } from "@/store/useTipDraftStore";
import {
  useHasLoyaltyContact,
  useLoyaltyActions,
} from "@/store/useLoyaltyStore";
import { CONTACT_BONUS_POINTS } from "@/config/loyalty";
import { isValidPhone } from "@/helpers/contactValue";
import { billsPath, orderPaths } from "@/routes/paths";
import styles from "./Order.module.scss";

function OrderConfirmedPage() {
  const { restaurantId = "", tableId = "" } = useParams<{
    restaurantId: string;
    tableId: string;
  }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const paths = orderPaths(restaurantId, tableId);

  // Emptying the cart here is also what stops a back-navigation into the
  // payment step from submitting the same order twice.
  useEffect(() => {
    useCartStore.getState().clear();
    // The tip has been taken, so who it was for goes with it.
    clearTipDraft();
  }, []);

  /**
   * The moment the programme is worth explaining: the guest has just paid and
   * can see what the points bought. Offered once, and only when the venue has
   * put a bonus behind it.
   */
  const hasContact = useHasLoyaltyContact();
  const { saveContact } = useLoyaltyActions();
  const [contactOpen, setContactOpen] = useState(false);
  const [phone, setPhone] = useState("");
  /**
   * The verdict from the last time the field was left, not a live one. Typing
   * clears it, so a guest fixing an address is not corrected mid-word.
   */
  const [phoneError, setPhoneError] = useState(false);
  const [awarded, setAwarded] = useState<number | null>(null);

  const phoneValid = phone.trim() === "" || isValidPhone(phone);
  const canSave = phone.trim() !== "" && phoneValid;

  const handleSaveContact = () => {
    if (!canSave) return;
    setAwarded(saveContact({ phone }));
    setContactOpen(false);
  };

  return (
    <>
      <Grid>
        <div className={`start-1 col-4 ${styles.orderWrapper}`}>
          <div className={styles.orderTerminalContent}>
            <i className={`las la-check-circle ${styles.orderTerminalIcon}`} />
            <p className={`headline-small ${styles.orderTerminalTitle}`}>
              {t("order.confirmed.title")}
            </p>
            <p className={`body-medium ${styles.orderTerminalDescription}`}>
              {t("order.confirmed.description")}
            </p>

            {awarded ? (
              <p className={`body-medium ${styles.orderTerminalDescription}`}>
                {t("loyalty.contactSaved", { count: awarded })}
              </p>
            ) : null}

            <div className={styles.orderTerminalActions}>
              {/* A link rather than a third button: this is an offer, and the
                  two below it are where the guest is actually going. */}
              {CONTACT_BONUS_POINTS > 0 && !hasContact ? (
                <TextComponent
                  text={t("loyalty.contactTitle", {
                    count: CONTACT_BONUS_POINTS,
                  })}
                  fontSize="body-bold-medium"
                  color="primary"
                  underline
                  uppercase
                  onClick={() => setContactOpen(true)}
                />
              ) : null}

              <Button
                onClick={() =>
                  navigate(billsPath(restaurantId, tableId), { replace: true })
                }
                label={t("order.button.backToBill")}
                variant="contained"
                color="primary"
                maxWidth
                mobile={true}
              />
              <Button
                onClick={() => navigate(paths.root, { replace: true })}
                label={t("order.button.orderMore")}
                variant="outlined"
                color="primary"
                maxWidth
                mobile={true}
              />
            </div>
          </div>
        </div>
      </Grid>

      <MuiDrawer
        sx={drawerStyles}
        anchor="bottom"
        open={contactOpen}
        onClose={() => setContactOpen(false)}
      >
        <Grid>
          <div
            className={`start-1 col-4 align-start ${drawerCss.drawerLayoutWrapper}`}
          >
            <div className={drawerCss.drawerLayoutHeader}>
              <IconButton
                iconName="las la-angle-left icon-big"
                onClick={() => setContactOpen(false)}
                color="primary"
              />
              <p className={`body-bold-large ${drawerCss.drawerLayoutTitle}`}>
                {t("loyalty.contactTitle", { count: CONTACT_BONUS_POINTS })}
              </p>
              <IconButton
                iconName=""
                onClick={() => {}}
                color="primary"
                variant="text"
              />
            </div>

            <div className={styles.contactPrompt}>
              <p className={`body-small ${styles.contactHint}`}>
                {t("loyalty.contactHint")}
              </p>

              <TextField
                id="loyalty-phone"
                label={t("loyalty.contactPhoneLabel")}
                placeholder={t("loyalty.contactPhonePlaceholder")}
                focused
                hasError={phoneError}
                helperText={
                  phoneError ? t("loyalty.contactPhoneInvalid") : undefined
                }
                field={{
                  name: "loyalty-phone",
                  value: phone,
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                    setPhone(event.target.value);
                    setPhoneError(false);
                  },
                  onBlur: () => setPhoneError(!phoneValid),
                }}
              />

              <Button
                onClick={handleSaveContact}
                label={t("loyalty.button.saveContact")}
                variant="contained"
                color="primary"
                maxWidth
                mobile={true}
                disabled={!canSave}
              />
            </div>
          </div>
        </Grid>
      </MuiDrawer>
    </>
  );
}

export default OrderConfirmedPage;
